import { yupResolver } from '@hookform/resolvers/yup';
import {
  Stack,
  Stepper,
  Step,
  StepLabel,
  Card,
  Button,
  Typography,
  Divider,
  Container,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useEffect, useMemo, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { QontoConnector } from './Steps/styles';
import QontoStepIcon from './Steps/QontoStepIcon';
import { FormProvider } from '#/components/shared/hook-form';
import { stepsData } from './Steps/content/stepsData';
import { ILivingWillForm } from '#/types/livingWill';
import useLocales from '../../../lib/hooks/useLocales';
import useStepper from '../../../lib/hooks/useStepper';
import EnoughForNowModal from '#/components/shared/ui/EnoughForNowModal';
import {
  allValuesReset,
  schemaValues,
  stepFourSchema,
  stepOneSchema,
  stepThreeSchema,
  stepTwoSchema,
} from '#/components/pages/LivingWill/schemaValidation';
import useAuth from '#/hooks/useAuth';
import {
  invalidateServicesStatsQueries,
  useGetServiceForm,
  useStartService,
} from '#/api/servicesQueries';
import Iconify from '#/components/shared/ui/Iconify';
import { useUpdateProfile } from '#/api/userQueries';
import useServiceStats from '#/hooks/useServiceStats';
import MarkAsDone from '#/components/shared/mark-as-done';
import { fToDB } from '#/utils/formatTime';
import RequiredBasicInfo from '../User/RequiredBasicInfo';

const LivingForm = () => {
  const { user, refetch } = useAuth();
  const { living_will, power_of_attorney } = useServiceStats();
  const { state } = useLocation();

  const { mutateAsync: updateProfile } = useUpdateProfile();

  const { data: dbFormValues } = useGetServiceForm('living-will');

  const { mutateAsync: initialiseLivingWill } = useStartService('livingwill');

  const [openEnoughNow, setOpenEnoughNow] = useState(false);

  const handleOpenEnoughNow = () => setOpenEnoughNow(true);

  const handleCloseEnoughNow = () => setOpenEnoughNow(false);

  const { handleNext, handleBack, activeStep, setActiveStep } = useStepper(
    !living_will?.service?.completed,
    'living_will',
    stepsData
  );

  const { translate } = useLocales();
  const navigate = useNavigate();

  const formSchemas = [
    stepOneSchema,
    stepTwoSchema,
    stepThreeSchema,
    stepFourSchema,
  ];

  const defaultValues = useMemo(
    () => schemaValues(user, dbFormValues, activeStep),
    [user, dbFormValues, activeStep]
  );

  const methods = useForm<ILivingWillForm>({
    resolver: formSchemas[activeStep]
      ? yupResolver(formSchemas[activeStep] as any)
      : undefined,
    defaultValues,
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
    reset,
    watch,
  } = methods;

  const submitForm = async ({
    data = watch(),
    startOver,
  }: {
    data: any;
    startOver?: boolean;
  }) => {
    const savingToast = toast(String(translate('global.saving')), {
      icon: <Iconify icon="arcticons:fastsave" />,
    });

    const setEmptyToNull = (obj: any): any => {
      if (obj && typeof obj === 'object') {
        if (Array.isArray(obj)) {
          return obj.map(setEmptyToNull);
        }
        return Object.fromEntries(
          Object.entries(obj).map(([key, value]) => {
            if (value === '' || value === undefined || value === null) {
              return [key, null];
            }
            return [key, setEmptyToNull(value)];
          })
        );
      }
      return obj;
    };

    const transformedData = setEmptyToNull(data);

    let reqData: any;

    if (startOver) {
      reqData = {
        ...transformedData,
        ...allValuesReset,
      };
    } else {
      reqData = {
        ...transformedData,
        date_of_birth: fToDB(data.date_of_birth),
        representative_inform_date: fToDB(data.representative_inform_date),
      };
    }

    if (activeStep === 0) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      await updateProfile({
        civil_status: reqData?.civil_status,
        date_of_birth: reqData?.date_of_birth,
        mobile_phone: user?.mobile_phone,
        first_name: defaultValues.first_name,
        last_name: defaultValues.last_name,
        address: {
          street: watch('street'),
          house_number: watch('house_number'),
          zip_code: watch('zip_code'),
          city: watch('city'),
          country: watch('country'),
        },
      });
      refetch();
    }
    await initialiseLivingWill(reqData).then(() => {
      toast.dismiss(savingToast);
      handleNext();
      invalidateServicesStatsQueries.getServiceForm('living-will');
      invalidateServicesStatsQueries.getServicesStats();
    });
    scrollToTop();
  };

  const onSubmit = async (data: any) => {
    await submitForm({ data });
  };

  const translatedStepsData = stepsData?.map((step, i) => ({
    ...step,
    label: translate(`livingWill.stepperInfo.${i}.label`),
    title: translate(`livingWill.stepperInfo.${i}.title`),
  }));

  const shortPainFree =
    activeStep === 0 || activeStep === 1 || activeStep === 2;

  const madeIt = activeStep === 3;

  useEffect(() => {
    if (dbFormValues || user) {
      reset(defaultValues);
    }
  }, [dbFormValues, user]);

  useEffect(
    () => () => {
      const submit = async () => {
        await submitForm({ data: watch() });
      };

      submit().then((r) => r);
    },
    []
  );

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const basicInfoMissing =
    user?.civil_status === null ||
    user?.civil_status === undefined ||
    user?.civil_status === '' ||
    user?.date_of_birth === null ||
    !user?.address;

  const hasNoPOA = !power_of_attorney?.service?.completed;

  useEffect(() => {
    if (state?.startOver) {
      setActiveStep(0);
      submitForm({ data: watch(), startOver: true });
    }
  }, [state?.startOver]);

  if (
    state?.step === 'markAsDone' ||
    state?.step === 'history' ||
    state?.step === 'uploadDocument'
  ) {
    return (
      <MarkAsDone
        serviceName="livingwill"
        serviceInvalidateKey="living-will"
        documentType="livingWill"
      />
    );
  }

  return (
    <Container maxWidth="lg">
      {shortPainFree && (
        <Typography variant="h2" mb={5} textAlign="center">
          {String(translate('livingWill.mainHeaders.shortPainFree'))}
        </Typography>
      )}
      {madeIt && (
        <Typography variant="h2" mb={5} textAlign="center">
          {String(translate('livingWill.mainHeaders.madeIt'))}
        </Typography>
      )}
      {/* {detailedLivingWill && ( */}
      {/*  <Typography variant="h2" mb={5} textAlign="center"> */}
      {/*    {String(translate('livingWill.mainHeaders.detailedLivingWill'))} */}
      {/*  </Typography> */}
      {/* )} */}
      <Stack sx={{ width: '100%' }} spacing={4}>
        {basicInfoMissing && activeStep === 0 && <RequiredBasicInfo />}
        {!basicInfoMissing && (
          <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
            <Stepper
              alternativeLabel
              activeStep={activeStep}
              connector={<QontoConnector />}
              sx={{
                display: {
                  xs: 'none',
                  sm: 'flex',
                },
              }}
            >
              {translatedStepsData?.map((step, i) => (
                <Step
                  key={`step-${step.title}`}
                  onClick={() => {
                    if (i < activeStep) {
                      setActiveStep(i);
                    }
                    if (living_will?.service?.completed) {
                      setActiveStep(i);
                    }
                  }}
                  sx={{
                    ...((i < activeStep || living_will?.service?.completed) && {
                      cursor: 'pointer',
                    }),
                  }}
                >
                  <StepLabel StepIconComponent={QontoStepIcon}>
                    {String(step.label)}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>

            <Card sx={{ p: 3, mt: 4 }}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="subtitle1" mb={2}>
                  {String(translatedStepsData[activeStep]?.title)}
                </Typography>
                {activeStep !== translatedStepsData.length - 1 && (
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    sx={{ mb: 2 }}
                    onClick={handleOpenEnoughNow}
                  >
                    <Typography
                      variant="body1"
                      color="error"
                      sx={{ cursor: 'pointer' }}
                    >
                      {String(translate('global.save'))}
                    </Typography>
                  </Button>
                )}
              </Stack>
              <Divider
                sx={{
                  mb: 3,
                }}
              />
              {translatedStepsData[activeStep]?.content(
                setActiveStep,
                dbFormValues
              )}
              <Divider
                sx={{
                  mt: 4,
                }}
              />
              <Stack
                direction={{
                  xs: 'column',
                  sm: 'row',
                }}
                justifyContent="flex-end"
                spacing={2}
                sx={{
                  mt: 3,
                }}
              >
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleBack}
                  disabled={activeStep === 0}
                >
                  <Typography>{String(translate('global.back'))}</Typography>
                </Button>
                {activeStep === translatedStepsData.length - 1 && (
                  <Button
                    variant="contained"
                    onClick={() => {
                      navigate('/dashboard/home');
                    }}
                    color="success"
                    sx={{
                      color: '#fff',
                    }}
                  >
                    <Typography>{String(translate('global.done'))}</Typography>
                  </Button>
                )}

                {activeStep !== translatedStepsData.length - 1 && (
                  <LoadingButton
                    variant="contained"
                    color="primary"
                    type="submit"
                    loading={isSubmitting}
                  >
                    <Typography>{String(translate('global.next'))}</Typography>
                  </LoadingButton>
                )}
                {hasNoPOA && activeStep === translatedStepsData.length - 1 && (
                  <LoadingButton
                    onClick={async () => {
                      if (activeStep === translatedStepsData.length - 1) {
                        navigate('/dashboard/vorsorgeauftrag');
                      }
                    }}
                    variant="contained"
                  >
                    <Typography>
                      {String(translate('global.continueToPOA'))}
                    </Typography>
                  </LoadingButton>
                )}
              </Stack>
            </Card>
          </FormProvider>
        )}
      </Stack>
      <EnoughForNowModal
        openEnoughNow={openEnoughNow}
        handleCloseEnoughNow={handleCloseEnoughNow}
      />
    </Container>
  );
};
export default LivingForm;
